import React, {useMemo, useState} from "react";
import {Stack, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {
    Dns as CardsIcon,
    East as HorizontalIcon,
    Schema as GraphIcon,
    South as VerticalIcon
} from "@mui/icons-material";
import {CardsView, GraphView} from "../views";
import {arrays, sorting} from "@atttomyx/shared-utils";
import {nodeUtils} from "../../utils";
import {LAYOUT_HORIZONTAL, LAYOUT_VERTICAL, VIEW_CARDS, VIEW_GRAPH} from "../../constants";
import "./peoplePage.css";

const sortByCategory = (a, b) => {
    return sorting.sortByField(a, b, "category");
};

const PeoplePage = (props) => {
    const { snackbar, dimensions, user, nodes, edges, filters,
        onSaveNode, onSaveEdge, onDeleteNode, onDeleteEdge } = props;

    const [ view, setView ] = useState(user.settings.view);
    const [ layout, setLayout ] = useState(user.settings.layout);

    const sanitizedEdges = useMemo(() => {
        const nodeIds = arrays.getIds(nodes);

        return edges.filter((edge) => arrays.contains(nodeIds, edge.sourceNodeId)
            && arrays.contains(nodeIds, edge.targetNodeId));
    }, [nodes, edges]);

    const categories = useMemo(() => {
        const map = {
            family: [
                "father",
                "mother",
                "husband",
                "wife",
                "son",
                "daughter",
                "brother",
                "sister",
                "uncle",
                "aunt",
                "cousin",
                "nephew",
                "niece",
                "father-in-law",
                "mother-in-law",
                "son-in-law",
                "daughter-in-law",
                "brother-in-law",
                "sister-in-law",
            ],
            friends: [
                "friend",
                "bestie",
                "girlfriend",
                "boyfriend",
            ],
            work: [
                "boss",
                "employee",
                "coworker",
                "vendor",
                "client",
            ],
        };

        sanitizedEdges.forEach((edge) => {
            const category = edge.category;

            if (!map[category]) {
                map[category] = [];
            }

            const list = map[category];
            const label = edge.label;

            if (!arrays.contains(list, label)) {
                arrays.addTo(list, label);
            }

            map[category] = list;
        });

        const list = [];

        Object.entries(map).forEach(([key, value]) => {
            arrays.addTo(list, {
                category: key,
                labels: value.sort(sorting.alphabetize),
            });
        });

        return list.sort(sortByCategory);
    }, [sanitizedEdges]);

    const people = useMemo(() => {
        return nodes.map(node => {
            return {
                id: node.id,
                imageUrl: node.imageUrl,
                firstName: node.firstName,
                lastName: node.lastName,
            }
        }).sort(sorting.sortByLastNameAndFirstName);
    }, [nodes]);

    const rootId = useMemo(() => {
        const root = nodes.find(node => nodeUtils.isRoot(node, user));

        return root ? root.id : null;
    }, [nodes]);

    const toggleView = (event, view) => {
        if (view) {
            setView(view);
        }
    };

    const toggleLayout = (event, layout) => {
        if (layout) {
            setLayout(layout);
        }
    };

    return <div className="people-page">
        {view === VIEW_GRAPH ?
            <GraphView
                snackbar={snackbar}
                dimensions={dimensions}
                user={user}
                nodes={nodes}
                edges={sanitizedEdges}
                horizontal={layout === LAYOUT_HORIZONTAL}
                categories={categories}
                people={people}
                rootId={rootId}
                onSaveNode={onSaveNode}
                onSaveEdge={onSaveEdge}
                onDeleteNode={onDeleteNode}
                onDeleteEdge={onDeleteEdge}
            /> :
            <CardsView
                snackbar={snackbar}
                dimensions={dimensions}
                user={user}
                nodes={nodes}
                filters={filters}
                horizontal={layout === LAYOUT_HORIZONTAL}
                categories={categories}
                people={people}
                rootId={rootId}
                onSaveNode={onSaveNode}
                onSaveEdge={onSaveEdge}
                onDeleteNode={onDeleteNode}
                onDeleteEdge={onDeleteEdge}
            />}
        <div className="react-flow__panel top right">
            <Stack spacing={2} direction="row">
                {view === VIEW_GRAPH ?
                    <ToggleButtonGroup
                        className="toggles"
                        color="primary"
                        value={layout}
                        exclusive
                        onChange={toggleLayout}
                    >
                        <ToggleButton
                            value={LAYOUT_HORIZONTAL}
                            title="Horizontal layout"
                        >
                            <HorizontalIcon/>
                        </ToggleButton>
                        <ToggleButton
                            value={LAYOUT_VERTICAL}
                            title="Vertical layout"
                        >
                            <VerticalIcon/>
                        </ToggleButton>
                    </ToggleButtonGroup> : null}
                <ToggleButtonGroup
                    className="toggles"
                    color="primary"
                    value={view}
                    exclusive
                    onChange={toggleView}
                >
                    <ToggleButton value={VIEW_GRAPH} title="Graph view">
                        <GraphIcon/>
                    </ToggleButton>
                    <ToggleButton value={VIEW_CARDS} title="Cards view">
                        <CardsIcon/>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>
        </div>
    </div>
};

export default PeoplePage;
